/**
 * Tunnel Metrics Component
 * 
 * Displays SSH tunnel telemetry and health metrics
 */

import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Zap, Key, RefreshCw } from 'lucide-react';
import edgeService from '../../services/edge.service';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useTenantStore } from '../../stores/tenantStore';

interface TunnelMetricsProps {
  edgeGuid: string;
}

interface TunnelHealth {
  connected: boolean;
  healthy: boolean;
  uptime: number;
  latency: number;
  keyAge: number;
  lastConnected: string | null;
  reconnections: number;
  restarts: number;
  bytesSent: number;
  bytesReceived: number;
}

export const TunnelMetrics: React.FC<TunnelMetricsProps> = ({ edgeGuid }) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [health, setHealth] = useState<TunnelHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const options = isBackground ? { showLoading: false } : undefined;
      const response = await edgeService.getSSHTunnelHealth(edgeGuid, options);
      setHealth(response.health);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tunnel health');
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth(false);
    const interval = setInterval(() => fetchHealth(true), 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [edgeGuid, selectedTenantId]);

  const formatUptime = (seconds: number): string => {
    if (seconds === 0) return '0s';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Activity className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading tunnel metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <div>
            <h3 className="font-semibold text-yellow-900">Metrics Unavailable</h3>
            <p className="text-sm text-yellow-700 mt-1">{error}</p>
            <p className="text-xs text-yellow-600 mt-2">
              Metrics will be available once the edge device starts reporting telemetry.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!health) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Connection Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Connection</span>
            {health.connected ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-600" />
            )}
          </div>
          <div className={`text-2xl font-bold ${health.connected ? 'text-green-600' : 'text-red-600'}`}>
            {health.connected ? 'Connected' : 'Disconnected'}
          </div>
        </div>

        {/* Health Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Health</span>
            {health.healthy ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            )}
          </div>
          <div className={`text-2xl font-bold ${health.healthy ? 'text-green-600' : 'text-yellow-600'}`}>
            {health.healthy ? 'Healthy' : 'Degraded'}
          </div>
        </div>

        {/* Uptime */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Uptime</span>
            <Clock className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatUptime(health.uptime)}
          </div>
        </div>

        {/* Latency */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Latency</span>
            <Zap className="h-5 w-5 text-purple-600" />
          </div>
          <div className={`text-2xl font-bold ${health.latency < 100 ? 'text-green-600' :
              health.latency < 300 ? 'text-yellow-600' : 'text-red-600'
            }`}>
            {health.latency}ms
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Detailed Metrics
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Reliability */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Reliability</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Reconnections</span>
                <span className="text-sm font-medium text-gray-900">{health.reconnections}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Restarts</span>
                <span className="text-sm font-medium text-gray-900">{health.restarts}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Last Connected</span>
                <span className="text-sm font-medium text-gray-900">
                  {health.lastConnected ? formatDate(health.lastConnected, useSettingsStore.getState().getDateFormatSettings()) : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {/* Network Traffic */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Network Traffic</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-gray-600">Sent</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{formatBytes(health.bytesSent)}</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">Received</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{formatBytes(health.bytesReceived)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatBytes(health.bytesSent + health.bytesReceived)}
                </span>
              </div>
            </div>
          </div>

          {/* Security */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Security</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-purple-600" />
                  <span className="text-sm text-gray-600">Key Age</span>
                </div>
                <span className={`text-sm font-medium ${health.keyAge < 60 ? 'text-green-600' :
                    health.keyAge < 90 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                  {health.keyAge} days
                </span>
              </div>
              {health.keyAge > 90 && (
                <div className="text-xs text-red-600 flex items-center gap-1 mt-2">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Key rotation recommended</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => fetchHealth(false)}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Metrics
        </button>

        {/* Grafana dashboard not yet wired up — /grafana/d/ssh-tunnels returns 404. Hide the button until it's available. */}
      </div>
    </div>
  );
};

export default TunnelMetrics;

